import React, { useState } from 'react';

// Enhanced markdown parser with full feature support
export class EnhancedMarkdownParser {
  static parseMetadata(content) {
    const metadataRegex = /^---\n([\s\S]*?)\n---\n([\s\S]*)$/;
    const match = content.match(metadataRegex);
    
    if (!match) {
      return { metadata: {}, content };
    }
    
    const [, metadataStr, bodyContent] = match;
    const metadata = {};
    
    metadataStr.split('\n').forEach(line => {
      const [key, ...valueParts] = line.split(':');
      if (key && valueParts.length > 0) {
        let value = valueParts.join(':').trim();
        
        // Remove quotes if present
        if (value.startsWith('"') && value.endsWith('"')) {
          value = value.slice(1, -1);
        }
        
        // Parse arrays
        if (value.startsWith('[') && value.endsWith(']')) {
          value = value.slice(1, -1).split(',').map(item => item.trim().replace(/"/g, ''));
        }
        
        // Parse boolean
        if (value === 'true') value = true;
        if (value === 'false') value = false;
        
        metadata[key.trim()] = value;
      }
    });
    
    return { metadata, content: bodyContent };
  }
  
  static parseContent(content) {
    let html = content;
    
    // Store original content for debugging
    console.log('Original content length:', content.length);
    
    // STEP 1: Protect and extract code blocks to prevent interference
    const codeBlocks = [];
    const codeBlockPlaceholder = '___CODE_BLOCK_PLACEHOLDER_';
    
    // Extract and store code blocks with placeholders
    html = html.replace(/```(\w*)\r?\n([\s\S]*?)\r?\n```/gm, (match, language, code) => {
      const index = codeBlocks.length;
      codeBlocks.push({
        language: language || 'text',
        code: code || '',
        originalMatch: match
      });
      return `\n${codeBlockPlaceholder}${index}${codeBlockPlaceholder}\n`;
    });
    
    console.log(`Extracted ${codeBlocks.length} code blocks for protection`);
    
    // STEP 2: Process all other markdown elements (safe from code block interference)
    
    // Process images
    html = html.replace(/!\[([^\]]*?)\]\(([^)\s]+)\)/gm, (match, alt, src) => {
      const cleanAlt = alt.trim();
      const cleanSrc = src.trim();
      
      let imageSrc = cleanSrc;
      if (!cleanSrc.startsWith('http') && !cleanSrc.startsWith('/')) {
        imageSrc = `/images/blog/${cleanSrc}`;
      }
      
      return `\n<div class="markdown-image-container">\n<img src="${imageSrc}" alt="${cleanAlt}" class="markdown-image" loading="lazy" onError="this.style.display='none'; this.nextElementSibling.style.display='block';" />\n<div class="image-placeholder" style="display:none; background: linear-gradient(45deg, #f8f9fa, #e9ecef); border: 2px dashed #dee2e6; border-radius: 8px; padding: 40px; text-align: center; color: #6c757d; margin: 20px 0;"><div style="font-size: 48px; margin-bottom: 16px;">🖼️</div><div><strong>Image not found:</strong><br><small>${imageSrc}</small></div></div>\n</div>\n`;
    });
    
    // Process tables
    html = this.parseTables(html);
    
    // Headers (with ID generation for TOC)
    html = html.replace(/^(#{1,6})\s*(.*$)/gim, (match, hashes, content) => {
      const level = hashes.length;
      const id = content.toLowerCase().replace(/[^a-z0-9\s]/g, '').replace(/\s+/g, '-');
      return `<h${level} id="${id}">${content}</h${level}>`;
    });
    
    // Text formatting
    html = html.replace(/\*\*\*(.*?)\*\*\*/g, '<strong><em>$1</em></strong>');
    html = html.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
    html = html.replace(/\*(.*?)\*/g, '<em>$1</em>');
    html = html.replace(/~~(.*?)~~/g, '<del>$1</del>');
    
    // Links
    html = html.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>');
    
    // Inline code
    html = html.replace(/`([^`]+)`/g, '<code class="inline-code">$1</code>');
    
    // Blockquotes
    html = html.replace(/^>\s*(.*$)/gim, '<blockquote class="markdown-blockquote">$1</blockquote>');
    
    // Lists
    html = this.parseLists(html);
    
    // Horizontal rules
    html = html.replace(/^---$/gim, '<hr class="markdown-hr">');
    
    // Line breaks and paragraphs
    html = this.parseLineBreaks(html);
    
    // Parse custom styling
    html = this.parseCustomStyling(html);
    
    // STEP 3: Restore code blocks with full processing
    html = html.replace(new RegExp(`${codeBlockPlaceholder}(\\d+)${codeBlockPlaceholder}`, 'g'), (match, index) => {
      const codeBlock = codeBlocks[parseInt(index)];
      if (!codeBlock) return match;
      
      return this.renderCodeBlock(codeBlock.language, codeBlock.code);
    });
    
    console.log('Final processed length:', html.length);
    return html;
  }
  
  static parseCodeBlocks(html) {
    // Enhanced code block parsing with collapse functionality and copy button
    // Use a more robust regex that properly captures multiline content
    const codeBlockRegex = /```(\w*)\r?\n([\s\S]*?)\r?\n```/gm;
    
    let matchCount = 0;
    const result = html.replace(codeBlockRegex, (match, language, code) => {
      matchCount++;
      console.log(`Processing code block ${matchCount}:`, {
        language: language || 'text',
        codeLength: code ? code.length : 0,
        firstChars: code ? code.substring(0, 50) : 'empty',
        lastChars: code ? code.substring(code.length - 50) : 'empty'
      });
      
      const lang = language || 'text';
      const codeId = 'code-' + Math.random().toString(36).substr(2, 9);
      
      // Clean and preserve the original code content
      const cleanCode = code || '';
      const lines = cleanCode.split('\n');
      const isCollapsible = lines.length > 3; // Collapse if more than 3 lines
      const escapedCode = this.escapeHtml(cleanCode);
      
      // Safely encode the raw code for the data attribute
      const encodedCode = cleanCode
        .replace(/&/g, '&amp;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;');
      
      return `\n<div class="code-block-container" data-language="${lang}">
  <div class="code-block-header">
    <div class="code-header-left">
      <span class="code-language">${lang}</span>
    </div>
    <div class="code-header-right">
      <button class="code-copy-btn" onclick="copyCodeBlock('${codeId}')" title="Copy code" data-code-id="${codeId}">
        <svg class="copy-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
          <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
        </svg>
        <svg class="check-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" style="display:none">
          <polyline points="20,6 9,17 4,12"></polyline>
        </svg>
        <span class="copy-text">Copy code</span>
        <span class="copied-text" style="display:none">Copied!</span>
      </button>${isCollapsible ? `
      <button class="code-collapse-btn" onclick="toggleCodeBlock('${codeId}')" data-collapsed="true">
        <span class="collapse-text" style="display:none">Collapse</span>
        <span class="expand-text">Expand</span>
        <span class="code-lines">(${lines.length} lines)</span>
      </button>` : ''}
    </div>
  </div>
  <pre class="code-block ${isCollapsible ? 'collapsible collapsed' : ''}" id="${codeId}" data-language="${lang}" data-code="${encodedCode}" style="${isCollapsible ? 'max-height: 100px; overflow: hidden; transition: max-height 0.3s ease;' : ''}"><code>${escapedCode}</code></pre>
</div>\n`;
    });
    
    console.log(`Total code blocks processed: ${matchCount}`);
    return result;
  }
  
  static renderCodeBlock(language, code) {
    // Render a single code block with all functionality
    const lang = language || 'text';
    const codeId = 'code-' + Math.random().toString(36).substr(2, 9);
    
    // Clean and preserve the original code content
    const cleanCode = code || '';
    const lines = cleanCode.split('\n');
    const isCollapsible = lines.length > 3; // Collapse if more than 3 lines
    const escapedCode = this.escapeHtml(cleanCode);
    
    // Safely encode the raw code for the data attribute
    const encodedCode = cleanCode
      .replace(/&/g, '&amp;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
    
    console.log(`Rendering code block: ${lang}, ${cleanCode.length} chars, first 50: "${cleanCode.substring(0, 50)}"`);
    
    return `\n<div class="code-block-container" data-language="${lang}">
  <div class="code-block-header">
    <div class="code-header-left">
      <span class="code-language">${lang}</span>
    </div>
    <div class="code-header-right">
      <button class="code-copy-btn" onclick="copyCodeBlock('${codeId}')" title="Copy code" data-code-id="${codeId}">
        <svg class="copy-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
          <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2 2v1"></path>
        </svg>
        <svg class="check-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" style="display:none">
          <polyline points="20,6 9,17 4,12"></polyline>
        </svg>
        <span class="copy-text">Copy code</span>
        <span class="copied-text" style="display:none">Copied!</span>
      </button>${isCollapsible ? `
      <button class="code-collapse-btn" onclick="toggleCodeBlock('${codeId}')" data-collapsed="true">
        <span class="collapse-text" style="display:none">Collapse</span>
        <span class="expand-text">Expand</span>
        <span class="code-lines">(${lines.length} lines)</span>
      </button>` : ''}
    </div>
  </div>
  <pre class="code-block ${isCollapsible ? 'collapsible collapsed' : ''}" id="${codeId}" data-language="${lang}" data-code="${encodedCode}" style="${isCollapsible ? 'max-height: 100px; overflow: hidden; transition: max-height 0.3s ease;' : ''}"><code>${escapedCode}</code></pre>
</div>\n`;
  }
  
  static parseTables(html) {
    // Enhanced table parsing
    const tableRegex = /\|(.+)\|\n\|(.+)\|\n((?:\|.+\|\n?)*)/g;
    
    return html.replace(tableRegex, (match, headerRow, separatorRow, bodyRows) => {
      const headers = headerRow.split('|').map(h => h.trim()).filter(h => h);
      const body = bodyRows.trim().split('\n').map(row => 
        row.split('|').map(cell => cell.trim()).filter(cell => cell)
      );
      
      let tableHtml = '<div class="table-responsive"><table class="markdown-table">';
      
      // Table header
      tableHtml += '<thead><tr>';
      headers.forEach(header => {
        tableHtml += `<th>${header}</th>`;
      });
      tableHtml += '</tr></thead>';
      
      // Table body
      tableHtml += '<tbody>';
      body.forEach(row => {
        if (row.length > 0) {
          tableHtml += '<tr>';
          row.forEach((cell, index) => {
            if (index < headers.length) {
              tableHtml += `<td>${cell}</td>`;
            }
          });
          tableHtml += '</tr>';
        }
      });
      tableHtml += '</tbody></table></div>';
      
      return tableHtml;
    });
  }
  
  static parseLists(html) {
    // Parse ordered lists
    html = html.replace(/^(\d+)\.\s+(.*$)/gim, '<oli>$2</oli>');
    
    // Parse unordered lists
    html = html.replace(/^[-*+]\s+(.*$)/gim, '<uli>$1</uli>');
    
    // Wrap consecutive list items
    html = html.replace(/(<oli>.*?<\/oli>[\s\n]*)+/gs, '<ol class="markdown-ol">$&</ol>');
    html = html.replace(/(<uli>.*?<\/uli>[\s\n]*)+/gs, '<ul class="markdown-ul">$&</ul>');
    
    // Convert temporary tags to proper li tags
    html = html.replace(/<oli>/g, '<li>').replace(/<\/oli>/g, '</li>');
    html = html.replace(/<uli>/g, '<li>').replace(/<\/uli>/g, '</li>');
    
    return html;
  }
  
  static parseLineBreaks(html) {
    // Split into blocks
    const blocks = html.split('\n\n');
    const processedBlocks = blocks.map(block => {
      const trimmed = block.trim();
      if (!trimmed) return '';
      
      // Skip if already wrapped in block elements
      if (trimmed.match(/^<(h[1-6]|div|pre|blockquote|ul|ol|table|hr)/i)) {
        return trimmed;
      }
      
      // Convert single line breaks to <br> within paragraphs
      const withBreaks = trimmed.replace(/\n/g, '<br>');
      return `<p>${withBreaks}</p>`;
    });
    
    return processedBlocks.filter(block => block).join('\n\n');
  }
  
  static parseCustomStyling(html) {
    // Enhanced gradient text parsing
    const gradientSpanRegex = /<span\s+style="([^"]*gradient[^"]*)"[^>]*>(.*?)<\/span>/gs;
    html = html.replace(gradientSpanRegex, (match, style, content) => {
      return `<span style="${style}" class="gradient-text">${content}</span>`;
    });
    
    // Enhanced styled divs with proper class assignment
    const styledDivRegex = /<div\s+style="([^"]*)"[^>]*>([\s\S]*?)<\/div>/gs;
    html = html.replace(styledDivRegex, (match, style, content) => {
      let className = 'styled-content';
      
      // Add specific classes based on styling
      if (style.includes('gradient')) className += ' gradient-bg';
      if (style.includes('border-radius')) className += ' rounded';
      if (style.includes('box-shadow')) className += ' shadowed';
      if (style.includes('text-align: center')) className += ' centered';
      
      return `<div style="${style}" class="${className}">${content}</div>`;
    });
    
    // Handle progress bars specifically
    const progressBarRegex = /<div\s+style="([^"]*background:\s*#e9ecef[^"]*)"[^>]*>\s*<div\s+style="([^"]*width:\s*(\d+)%[^"]*)"[^>]*>([^<]*)<\/div>\s*<\/div>/gs;
    html = html.replace(progressBarRegex, (match, containerStyle, barStyle, percentage, label) => {
      return `
        <div class="progress-container" style="${containerStyle}">
          <div class="progress-bar" style="${barStyle}" data-percentage="${percentage}">
            <span class="progress-label">${label}</span>
          </div>
        </div>
      `;
    });
    
    return html;
  }
  
  static escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }
  
  static createTOC(content) {
    const headers = [];
    const headerRegex = /^(#{1,6})\s*(.*)/gm;
    let match;
    
    while ((match = headerRegex.exec(content)) !== null) {
      const level = match[1].length;
      const text = match[2].trim();
      const id = text.toLowerCase().replace(/[^a-z0-9\s]/g, '').replace(/\s+/g, '-');
      
      headers.push({
        level,
        text,
        id
      });
    }
    
    return headers;
  }
  
  static estimateReadingTime(content) {
    const wordsPerMinute = 200;
    const wordCount = content.split(/\s+/).length;
    const readingTime = Math.ceil(wordCount / wordsPerMinute);
    return readingTime;
  }
}

// Collapsible Code Block Component
export const CollapsibleCodeBlock = ({ code, language, lines }) => {
  const [isCollapsed, setIsCollapsed] = useState(lines > 10);
  
  return (
    <div className="code-block-container" data-language={language}>
      <div className="code-block-header">
        <span className="code-language">{language}</span>
        {lines > 10 && (
          <button 
            className="code-collapse-btn" 
            onClick={() => setIsCollapsed(!isCollapsed)}
          >
            {isCollapsed ? 'Expand' : 'Collapse'}
            <span className="code-lines">({lines} lines)</span>
          </button>
        )}
      </div>
      <pre className={`code-block ${isCollapsed ? 'collapsed' : ''}`}>
        <code dangerouslySetInnerHTML={{ __html: code }} />
      </pre>
    </div>
  );
};

// Enhanced React component for rendering markdown
export const EnhancedMarkdownRenderer = ({ content, className = '', showMetadata = true }) => {
  const { metadata, content: markdownContent } = EnhancedMarkdownParser.parseMetadata(content);
  const html = EnhancedMarkdownParser.parseContent(markdownContent);
  const readingTime = EnhancedMarkdownParser.estimateReadingTime(markdownContent);
  
  // Add global code block toggle and copy functions
  React.useEffect(() => {
    window.toggleCodeBlock = (id) => {
      const codeBlock = document.getElementById(id);
      const button = document.querySelector(`button[onclick="toggleCodeBlock('${id}')"]`);
      
      if (codeBlock && button) {
        const isCollapsed = button.getAttribute('data-collapsed') === 'true';
        
        if (isCollapsed) {
          codeBlock.style.maxHeight = 'none';
          codeBlock.classList.remove('collapsed');
          button.querySelector('.collapse-text').style.display = 'inline';
          button.querySelector('.expand-text').style.display = 'none';
          button.setAttribute('data-collapsed', 'false');
        } else {
          codeBlock.style.maxHeight = '100px';
          codeBlock.classList.add('collapsed');
          button.querySelector('.collapse-text').style.display = 'none';
          button.querySelector('.expand-text').style.display = 'inline';
          button.setAttribute('data-collapsed', 'true');
        }
      }
    };
    
    window.copyCodeBlock = async (id) => {
      const codeBlock = document.getElementById(id);
      const button = document.querySelector(`button[data-code-id="${id}"]`);
      
      if (codeBlock && button) {
        try {
          // Get the raw code from data attribute or extract from code element
          let codeText = codeBlock.getAttribute('data-code');
          if (codeText) {
            // Unescape HTML entities in correct order (amp must be last)
            codeText = codeText
              .replace(/&lt;/g, '<')
              .replace(/&gt;/g, '>')
              .replace(/&quot;/g, '"')
              .replace(/&#39;/g, "'")
              .replace(/&amp;/g, '&');
          } else {
            const codeElement = codeBlock.querySelector('code');
            codeText = codeElement ? codeElement.textContent : codeBlock.textContent;
          }
          
          await navigator.clipboard.writeText(codeText);
          
          // Show copied feedback with icon animation
          const copyIcon = button.querySelector('.copy-icon');
          const checkIcon = button.querySelector('.check-icon');
          const copyText = button.querySelector('.copy-text');
          const copiedText = button.querySelector('.copied-text');
          
          // Hide copy icon, show check icon
          copyIcon.style.display = 'none';
          checkIcon.style.display = 'block';
          copyText.style.display = 'none';
          copiedText.style.display = 'inline';
          button.classList.add('copied');
          
          // Reset after 2 seconds
          setTimeout(() => {
            copyIcon.style.display = 'block';
            checkIcon.style.display = 'none';
            copyText.style.display = 'inline';
            copiedText.style.display = 'none';
            button.classList.remove('copied');
          }, 2000);
        } catch (err) {
          console.error('Failed to copy code:', err);
          // Fallback for older browsers
          try {
            const textArea = document.createElement('textarea');
            let fallbackCodeText = codeBlock.getAttribute('data-code');
            if (fallbackCodeText) {
              // Unescape HTML entities in correct order (amp must be last)
              fallbackCodeText = fallbackCodeText
                .replace(/&lt;/g, '<')
                .replace(/&gt;/g, '>')
                .replace(/&quot;/g, '"')
                .replace(/&#39;/g, "'")
                .replace(/&amp;/g, '&');
              textArea.value = fallbackCodeText;
            } else {
              const codeElement = codeBlock.querySelector('code');
              textArea.value = codeElement ? codeElement.textContent : codeBlock.textContent;
            }
            textArea.style.position = 'fixed';
            textArea.style.left = '-999999px';
            textArea.style.top = '-999999px';
            document.body.appendChild(textArea);
            textArea.focus();
            textArea.select();
            document.execCommand('copy');
            document.body.removeChild(textArea);
            
            // Show success feedback even for fallback
            const copyIcon = button.querySelector('.copy-icon');
            const checkIcon = button.querySelector('.check-icon');
            const copyText = button.querySelector('.copy-text');
            const copiedText = button.querySelector('.copied-text');
            
            copyIcon.style.display = 'none';
            checkIcon.style.display = 'block';
            copyText.style.display = 'none';
            copiedText.style.display = 'inline';
            button.classList.add('copied');
            
            setTimeout(() => {
              copyIcon.style.display = 'block';
              checkIcon.style.display = 'none';
              copyText.style.display = 'inline';
              copiedText.style.display = 'none';
              button.classList.remove('copied');
            }, 2000);
          } catch (fallbackErr) {
            console.error('Fallback copy also failed:', fallbackErr);
          }
        }
      }
    };
    
    return () => {
      delete window.toggleCodeBlock;
      delete window.copyCodeBlock;
    };
  }, []);
  
  return (
    <div className={`markdown-content enhanced ${className}`}>
      {showMetadata && metadata.title && (
        <div className="blog-metadata">
          <h1 className="blog-title">{metadata.title}</h1>
          {metadata.description && (
            <p className="blog-description">{metadata.description}</p>
          )}
          <div className="blog-meta">
            {metadata.date && <span className="blog-date">{metadata.date}</span>}
            {metadata.author && <span className="blog-author">By {metadata.author}</span>}
            <span className="reading-time">{readingTime} min read</span>
          </div>
          {metadata.tags && (
            <div className="blog-tags">
              {metadata.tags.map(tag => (
                <span key={tag} className="blog-tag">{tag}</span>
              ))}
            </div>
          )}
        </div>
      )}
      <div 
        className="markdown-body enhanced"
        dangerouslySetInnerHTML={{ __html: html }}
      />
    </div>
  );
};

export default EnhancedMarkdownParser;